import type { AgendaItem, ParsedAgenda } from '@/types';

// Common stop words to filter out
const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been',
  'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
  'could', 'should', 'may', 'might', 'must', 'shall', 'can', 'need',
  'our', 'your', 'their', 'this', 'that', 'these', 'those', 'about',
  'how', 'what', 'when', 'where', 'why', 'which', 'who', 'whom',
  'also', 'just', 'only', 'very', 'really', 'more', 'most', 'some',
  'any', 'all', 'both', 'each', 'every', 'other', 'such', 'no', 'not',
]);

/**
 * Extract meaningful keywords from text
 */
export function extractKeywords(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter(word => word.length > 2 && !STOP_WORDS.has(word));
}

/**
 * Determine the indentation level of a line
 * Returns: 0 for group headers, 1 for items, 2 for sub-items
 */
function getIndentLevel(line: string): number {
  const trimmed = line.trim();

  // Count leading whitespace, treating tabs as 4 spaces
  const leadingWhitespace = line.match(/^[\t ]*/)?.[0] || '';
  const tabCount = (leadingWhitespace.match(/\t/g) || []).length;
  const spaceCount = (leadingWhitespace.match(/ /g) || []).length;
  const effectiveSpaces = (tabCount * 4) + spaceCount; // Tab = 4 spaces
  const indentLevel = Math.floor(effectiveSpaces / 2); // 2 spaces = 1 level

  // Check for multi-dash prefix (-- or --- for deeper nesting)
  const multiDashMatch = trimmed.match(/^(-{2,})\s/);
  if (multiDashMatch) {
    return Math.min(multiDashMatch[1].length, 3); // Cap at level 3
  }

  // Check for single dash/bullet with leading whitespace
  if (/^[-*•◦▪▸]\s/.test(trimmed)) {
    return Math.max(1, indentLevel); // At least level 1 for bulleted items
  }

  // Non-bulleted lines with significant indent
  if (indentLevel >= 2) {
    return Math.min(indentLevel, 3); // Cap at level 3
  }

  return indentLevel;
}

/**
 * Detect if a line is a group header (topic/section)
 */
function isGroupHeaderLine(line: string): boolean {
  const trimmed = line.trim();

  // Empty lines are not headers
  if (!trimmed) return false;

  // Lines starting with bullets/dashes are NOT headers
  if (/^[-*•◦▪▸]/.test(trimmed)) return false;

  // Lines starting with numbers (1. 2. etc) are NOT headers
  if (/^[\d]+[.)]/.test(trimmed)) return false;

  // Lines starting with semantic sub-item indicators are NOT headers
  if (/^(Example|Important|Note|Why|How|Tip|Warning|Info):/i.test(trimmed)) return false;

  // ALL CAPS lines (at least 3 chars, majority uppercase) are headers
  const letters = trimmed.replace(/[^a-zA-Z]/g, '');
  if (letters.length >= 3) {
    const upperCount = (trimmed.match(/[A-Z]/g) || []).length;
    const lowerCount = (trimmed.match(/[a-z]/g) || []).length;
    if (upperCount > 0 && lowerCount === 0) {
      return true; // Pure ALL CAPS
    }
    if (upperCount > lowerCount * 2) {
      return true; // Mostly caps (allows for "Topic A:" style)
    }
  }

  // Lines ending with colon are headers
  if (/:\s*$/.test(trimmed)) return true;

  // Pattern: "Topic name - description" (common in docs/presentations)
  // But only if the part before dash is short (< 30 chars) and capitalized
  const dashMatch = trimmed.match(/^([A-Z][^-]+)\s*-\s*.+/);
  if (dashMatch && dashMatch[1].trim().length < 30) {
    return true;
  }

  // Short lines (< 40 chars) without punctuation that aren't indented = likely header
  const hasLeadingWhitespace = /^[\t ]/.test(line);
  if (!hasLeadingWhitespace && trimmed.length < 40 && !/[.!?]$/.test(trimmed)) {
    // Check if it looks like a title (starts with capital, no sentence structure)
    if (/^[A-Z]/.test(trimmed) && !/^(I |We |You |The |A |An |This |That |First|Second|Third)/.test(trimmed)) {
      return true;
    }
  }

  return false;
}

/**
 * Detect if a line should be indented as a sub-item based on semantic content
 */
function getSemanticIndentLevel(line: string): number {
  const trimmed = line.trim();

  // Lines starting with "Example:", "Important:", etc. are sub-items
  if (/^(Example|Important|Note|Why|How|Tip|Warning|Info):/i.test(trimmed)) {
    return 2;
  }

  // Lines starting with "First,", "Second,", etc. indicating explanation
  if (/^(First|Second|Third|Finally|Also|Additionally),/i.test(trimmed)) {
    return 1;
  }

  return 0;
}

/**
 * Clean a single line of agenda text (remove bullets, numbers, etc.)
 */
function cleanAgendaLine(line: string): string {
  return line
    .trim()
    .replace(/^-{2,}\s*/, '')            // Multi-dashes (-- or ---)
    .replace(/^[-*•◦▪▸]\s*/, '')         // Single bullets
    .replace(/^[\d]+[.)]\s*/, '')         // Numbers
    .replace(/^[a-z][.)]\s*/i, '')        // Letters
    .replace(/^\[[\sx]?\]\s*/i, '')       // Checkboxes
    .replace(/^[☐☑✓✗]\s*/, '')           // Unicode checkboxes
    .trim();
}

/**
 * Parse raw agenda text into structured items with hierarchy
 */
export function parseAgendaText(rawText: string): ParsedAgenda {
  const rawLines = rawText.split('\n');
  const items: AgendaItem[] = [];

  let currentGroupId: string | undefined = undefined;
  let itemIndex = 0;

  for (const rawLine of rawLines) {
    // Skip completely empty lines
    if (!rawLine.trim()) continue;

    const isHeader = isGroupHeaderLine(rawLine);

    // Determine indent level from whitespace OR semantic patterns
    let indentLevel = 0;
    if (!isHeader) {
      const whitespaceIndent = getIndentLevel(rawLine);
      const semanticIndent = getSemanticIndentLevel(rawLine);
      indentLevel = Math.max(whitespaceIndent, semanticIndent, 1); // At least level 1 for non-headers
    }

    const cleanedText = cleanAgendaLine(rawLine);

    // Skip if cleaning resulted in empty text
    if (!cleanedText) continue;

    const id = `item-${itemIndex}-${Date.now()}`;

    // If this is a header, create a new group
    if (isHeader) {
      currentGroupId = `group-${itemIndex}-${Date.now()}`;
    }

    items.push({
      id,
      text: cleanedText,
      keywords: isHeader ? [] : extractKeywords(cleanedText), // Headers don't need keywords for matching
      covered: false,
      coveredAt: null,
      confidence: 0,
      level: indentLevel,
      isGroupHeader: isHeader,
      groupId: isHeader ? undefined : currentGroupId,
    });

    itemIndex++;
  }

  return {
    items,
    createdAt: Date.now(),
  };
}
